import { useState } from 'react';
import { Check, Copy, Link2, Loader2, RefreshCw, X } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { StatusBadge } from '@/components/ui/StatusBadge';
import { QrCode } from '@/components/admin/QrCode';
import {
  type OnboardingAskFor,
  useCreateOnboardingLink,
  useDeactivateOnboardingLink,
  useOnboardingLinks,
  useRotateOnboardingLink,
  useUpdateOnboardingLink,
} from '@/hooks/admin/useOnboardingLinks';

/**
 * The chain's public new-store link, on the group master's Overview tab.
 *
 * ONE link per chain, not one per store: corporate pastes this single URL into
 * its own onboarding packet and every store of the chain uses it. Rotate
 * exists because a URL that lives in email threads and printed packets
 * eventually leaks — the fix is a new token, and the old one stops working.
 */

const ASK_LABELS: Array<{ key: keyof OnboardingAskFor; label: string }> = [
  { key: 'store_code', label: 'Store number' },
  { key: 'store_phone', label: 'Store phone' },
  { key: 'gm_contact', label: 'Frame contact as the GM' },
  { key: 'open_date', label: 'Opening date' },
  { key: 'delivery_days', label: 'Preferred delivery days' },
  { key: 'special_instructions', label: 'Driver instructions' },
  { key: 'terms', label: 'Terms & conditions' },
  { key: 'signature', label: 'Signature' },
  // Franchise links get this ON without anyone ticking it (resolved from the
  // master's group_kind); ticking it here turns it on for a chain link too.
  { key: 'equipment', label: 'Equipment + lease handoff' },
];

function fmtWhen(iso: string | null): string {
  if (!iso) return 'never';
  return new Date(iso).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

export function OnboardingLinkPanel({
  customerId,
  chainLabel,
}: {
  customerId: string;
  chainLabel: string;
}) {
  const { data, isLoading } = useOnboardingLinks(customerId);
  const create = useCreateOnboardingLink(customerId);
  const update = useUpdateOnboardingLink(customerId);
  const rotate = useRotateOnboardingLink(customerId);
  const deactivate = useDeactivateOnboardingLink(customerId);

  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const links = data?.links ?? [];
  const active = links.find((l) => l.state === 'ok') ?? null;
  const retired = links.filter((l) => l.state !== 'ok');

  const copy = async (url: string) => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      setError('Copy failed — select the link above and copy it by hand.');
    }
  };

  const run = (p: Promise<unknown>) => {
    setError(null);
    p.catch((e: unknown) => setError(e instanceof Error ? e.message : 'Something went wrong.'));
  };

  return (
    <div className="rounded-lg border border-brix-border bg-brix-surface-alt/30 px-3.5 py-3 space-y-3">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <p className="text-[13px] font-semibold text-brix-ink inline-flex items-center gap-1.5">
          <Link2 size={13} className="text-brix-primary" />
          New-store link
        </p>
        {active && (
          <StatusBadge tone="success" size="sm">
            Active · used {active.uses_count}×
          </StatusBadge>
        )}
      </div>

      <p className="text-[12px] text-brix-muted">
        One public link for the whole chain — hand it to corporate to put in their onboarding
        packet. A store manager opens it, says which store they are, and the application lands in
        the Onboarding queue already attached to {chainLabel}. They are not asked for anything the
        master account already settles: terms, payment, billing address or pricing.
      </p>

      {isLoading ? (
        <div className="text-[13px] text-brix-muted inline-flex items-center gap-2">
          <Loader2 size={13} className="animate-spin" /> Loading…
        </div>
      ) : !active ? (
        <div className="flex items-center gap-3 flex-wrap">
          <Button
            variant="secondary"
            size="sm"
            disabled={create.isPending}
            onClick={() => run(create.mutateAsync(undefined))}
          >
            {create.isPending ? <Loader2 size={13} className="animate-spin" /> : <Link2 size={13} />}
            Create the link
          </Button>
          <span className="text-[12px] text-brix-muted">
            {retired.length > 0
              ? 'The previous link was revoked — mint a new one to hand out.'
              : 'No link yet.'}
          </span>
        </div>
      ) : (
        <div className="space-y-3">
          <div className="flex items-start gap-4 flex-wrap">
            <div className="min-w-[16rem] flex-1 space-y-2">
              <div className="flex items-center gap-2">
                <code className="flex-1 rounded-lg border border-brix-border bg-brix-surface px-2.5 py-2 text-[12px] font-mono text-brix-ink break-all">
                  {active.url}
                </code>
                <Button variant="secondary" size="sm" onClick={() => void copy(active.url)}>
                  {copied ? <Check size={13} /> : <Copy size={13} />}
                  {copied ? 'Copied' : 'Copy'}
                </Button>
              </div>
              <p className="text-[12px] text-brix-muted">
                Created {fmtWhen(active.created_at)}
                {active.created_by ? ` by ${active.created_by}` : ''} · last used{' '}
                {fmtWhen(active.last_used_at)}
                {active.expires_at ? ` · expires ${fmtWhen(active.expires_at)}` : ''}
              </p>
            </div>
            <div className="shrink-0 rounded-lg border border-brix-border bg-white p-2">
              <QrCode value={active.url} size={124} />
            </div>
          </div>

          {/* What the form asks for */}
          <div>
            <p className="text-[12px] font-semibold text-brix-muted mb-1.5">The form asks for</p>
            <div className="flex flex-wrap gap-x-4 gap-y-1.5">
              {ASK_LABELS.map(({ key, label }) => (
                <label
                  key={key}
                  className="inline-flex items-center gap-1.5 text-[12px] text-brix-ink cursor-pointer"
                >
                  <input
                    type="checkbox"
                    className="accent-brix-primary"
                    checked={active.ask_for[key]}
                    disabled={update.isPending}
                    onChange={(e) =>
                      run(
                        update.mutateAsync({
                          link_id: active.id,
                          ask_for: { ...active.ask_for, [key]: e.target.checked },
                        }),
                      )
                    }
                  />
                  {label}
                </label>
              ))}
            </div>
            <p className="text-[11px] text-brix-muted mt-1.5">
              Store name, delivery address and a contact are always asked for. Terms and signature
              are off by default — the master account signed, and a store manager can't accept
              terms on the chain's behalf.
            </p>
          </div>

          <div className="flex items-center gap-3 flex-wrap">
            <Button
              variant="secondary"
              size="sm"
              disabled={rotate.isPending}
              onClick={() => {
                if (
                  window.confirm(
                    'Replace this link with a new one? The current URL stops working immediately — ' +
                      "anyone holding it (including corporate's packet) will need the new one.",
                  )
                ) {
                  run(rotate.mutateAsync({ link_id: active.id }));
                }
              }}
            >
              {rotate.isPending ? (
                <Loader2 size={13} className="animate-spin" />
              ) : (
                <RefreshCw size={13} />
              )}
              Rotate
            </Button>
            <button
              type="button"
              disabled={deactivate.isPending}
              className="text-[12px] text-brix-muted underline cursor-pointer hover:text-brix-ink"
              onClick={() => {
                if (window.confirm('Turn this link off? Nobody will be able to file a new store through it.')) {
                  run(deactivate.mutateAsync({ link_id: active.id }));
                }
              }}
            >
              Turn it off
            </button>
          </div>
        </div>
      )}

      {retired.length > 0 && (
        <p className="text-[11px] text-brix-muted">
          {retired.length} retired link{retired.length === 1 ? '' : 's'} kept on record (
          {retired.reduce((n, l) => n + l.uses_count, 0)} store
          {retired.reduce((n, l) => n + l.uses_count, 0) === 1 ? '' : 's'} filed through them).
        </p>
      )}

      {error && (
        <div className="rounded-lg border border-brix-warning/40 bg-brix-warning/5 px-3 py-2 text-[12px] text-brix-warning inline-flex items-start gap-2">
          <X size={13} className="mt-0.5 shrink-0" />
          {error}
        </div>
      )}
    </div>
  );
}
